// src/pages/ventes/ImportClients.jsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, Row, Col, Form, Button, Table, Badge, Alert, Spinner } from "react-bootstrap";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import * as XLSX from 'xlsx';
import { formatCurrency } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   STEPPER
   ============================================================ */
const Stepper = ({ step, steps }) => (
    <div className="stepper-modern mb-4">
        <div className="d-flex justify-content-between align-items-center position-relative">
            {steps.map((label, index) => {
                const stepNumber = index + 1;
                const isActive = step === stepNumber;
                const isCompleted = step > stepNumber;
                return (
                    <React.Fragment key={index}>
                        <div className={`step-item-charte ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                            <div className="step-number-charte">
                                {isCompleted ? <i className="ri-check-line"></i> : stepNumber}
                            </div>
                            <div className="step-label-charte">{label}</div>
                        </div>
                        {index < steps.length - 1 && (
                            <div className={`step-line-charte ${step > stepNumber ? 'completed' : ''}`}></div>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    </div>
);

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function ImportClients() {
    const navigate = useNavigate();
    const [importStep, setImportStep] = useState(1);
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreview, setFilePreview] = useState([]);
    const [validationResults, setValidationResults] = useState({});
    const [importProgress, setImportProgress] = useState(0);
    const [importStatus, setImportStatus] = useState('idle');
    const [importStats, setImportStats] = useState({});
    const [importing, setImporting] = useState(false);
    const [uploadResults, setUploadResults] = useState(null);
    const [defaultType, setDefaultType] = useState('entreprise');
    const [dragOver, setDragOver] = useState(false);
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });

    const expectedColumns = [
        { key: 'code', label: 'Code client', required: false, example: 'CLI2024001' },
        { key: 'raison_sociale', label: 'Raison sociale / Nom', required: true, example: 'ETS MBOUMBA' },
        { key: 'telephone', label: 'Téléphone', required: true, example: '24176542759' },
        { key: 'email', label: 'Email', required: false, example: 'contact@ets.com' },
        { key: 'adresse', label: 'Adresse', required: false, example: 'Libreville, BP 1234' },
        { key: 'ice', label: 'ICE', required: false, example: '12345678' },
        { key: 'rc', label: 'RC', required: false, example: 'RC/2024/001' }
    ];

    /* ============================================================
       TEMPLATES
       ============================================================ */
    const downloadTemplateCSV = () => {
        const headers = ['code', 'raison_sociale', 'telephone', 'email', 'adresse', 'ice', 'rc'];
        const examples = ['CLI2024001', 'ETS MBOUMBA', '24176542759', 'contact@ets.com', 'Libreville, BP 1234', '12345678', 'RC/2024/001'];
        const csvContent = [
            headers.join(','),
            examples.join(','),
            ',NDONG Pierre,24177513526,pierre.ndong@gmail.com,Libreville,,',
            ',MARTIN SARL,24177012345,martin@sarl.com,Port-Gentil,87654321,RC/2024/002'
        ].join('\n');

        const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "modele-import-clients.csv";
        link.click();
        showToast('success', 'Modèle téléchargé', 'Le modèle CSV a été téléchargé avec succès');
    };

    const downloadTemplateExcel = () => {
        const data = [
            ['code', 'raison_sociale', 'telephone', 'email', 'adresse', 'ice', 'rc'],
            ['CLI2024001', 'ETS MBOUMBA', '24176542759', 'contact@ets.com', 'Libreville, BP 1234', '12345678', 'RC/2024/001'],
            ['', 'NDONG Pierre', '24177513526', 'pierre.ndong@gmail.com', 'Libreville', '', ''],
            ['', 'MARTIN SARL', '24177012345', 'martin@sarl.com', 'Port-Gentil', '87654321', 'RC/2024/002'],
        ];
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Clients");
        XLSX.writeFile(workbook, "modele-import-clients.xlsx");
        showToast('success', 'Modèle téléchargé', 'Le modèle Excel a été téléchargé avec succès');
    };

    /* ============================================================
       FILE SELECT
       ============================================================ */
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedExtensions = ['.csv', '.xls', '.xlsx'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        if (!allowedExtensions.includes(fileExtension)) {
            showToast('error', 'Format invalide', 'Format de fichier non supporté. Utilisez CSV ou Excel.');
            return;
        }

        setSelectedFile(file);
        previewFile(file);
    };

    const previewFile = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                let previewData = [];
                if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
                    const content = e.target.result;
                    const lines = content.split('\n').slice(0, 11);
                    previewData = lines.map(line =>
                        line.split(',').map(cell => cell.trim().replace(/^"|"$/g, ''))
                    );
                } else {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    previewData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }).slice(0, 11);
                }
                setFilePreview(previewData);
                if (previewData.length > 0) validateFileData(previewData);
            } catch (error) {
                console.error('Erreur lecture fichier:', error);
                showToast('error', 'Erreur', 'Erreur lors de la lecture du fichier');
            }
        };
        if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
            reader.readAsText(file, 'UTF-8');
        } else {
            reader.readAsArrayBuffer(file);
        }
    };

    /* ============================================================
       VALIDATION
       ============================================================ */
    const isValidPhoneNumber = (numero) => {
        if (!numero) return false;
        const cleaned = numero.toString().replace(/\s+/g, '');
        const gabonRegex = /^241[0-9]{8}$/;
        const internationalRegex = /^\+?[0-9]{10,15}$/;
        return gabonRegex.test(cleaned) || internationalRegex.test(cleaned);
    };

    const isValidEmail = (email) => {
        if (!email) return true;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const validateFileData = (previewData) => {
        const results = {
            valid: 0, invalid: 0, duplicates: 0,
            errors: [], columnMatch: true, warnings: []
        };

        if (previewData.length > 0) {
            const headers = previewData[0];
            const requiredHeaders = ['raison_sociale', 'telephone'];
            const hasRequiredHeaders = requiredHeaders.every(required =>
                headers.some(h => h && h.toLowerCase().trim() === required)
            );
            if (!hasRequiredHeaders) {
                results.columnMatch = false;
                results.errors.push("Les colonnes 'raison_sociale' et 'telephone' sont obligatoires dans le fichier.");
            }
        }

        if (previewData.length > 1 && results.columnMatch) {
            const phoneNumbers = new Set();
            previewData.slice(1).forEach((row, index) => {
                const raisonSociale = row[1]?.toString().trim();
                const telephone = row[2]?.toString().trim();
                const email = row[3]?.toString().trim();

                if (!raisonSociale) {
                    results.invalid++;
                    results.errors.push(`Ligne ${index + 2}: Raison sociale / Nom manquant`);
                } else if (!telephone) {
                    results.invalid++;
                    results.errors.push(`Ligne ${index + 2}: Téléphone manquant pour "${raisonSociale}"`);
                } else if (!isValidPhoneNumber(telephone)) {
                    results.invalid++;
                    results.errors.push(`Ligne ${index + 2}: Numéro invalide "${telephone}" pour "${raisonSociale}"`);
                } else if (email && !isValidEmail(email)) {
                    results.invalid++;
                    results.errors.push(`Ligne ${index + 2}: Email invalide "${email}" pour "${raisonSociale}"`);
                } else {
                    if (phoneNumbers.has(telephone)) {
                        results.duplicates++;
                        results.warnings.push(`Ligne ${index + 2}: Téléphone en doublon "${telephone}" pour "${raisonSociale}"`);
                    } else {
                        phoneNumbers.add(telephone);
                    }
                    if (!row[0] || !row[0].toString().trim()) {
                        results.warnings.push(`Ligne ${index + 2}: Code client manquant - sera traité comme ${defaultType === 'particulier' ? 'particulier' : 'entreprise'}`);
                    }
                    results.valid++;
                }
            });
        }

        setValidationResults(results);
        setImportStep(2);
    };

    /* ============================================================
       IMPORT
       ============================================================ */
    const importClients = async () => {
        if (!selectedFile) {
            showToast('error', 'Erreur', 'Aucun fichier sélectionné');
            return;
        }
        setImporting(true);
        setImportStatus('importing');
        setImportProgress(0);

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('defaultType', defaultType);

            const progressInterval = setInterval(() => {
                setImportProgress(prev => {
                    if (prev >= 90) { clearInterval(progressInterval); return 90; }
                    return prev + 10;
                });
            }, 300);

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/import_clients.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            clearInterval(progressInterval);

            if (response.ok) {
                setImportProgress(100);
                try {
                    const result = await response.json();
                    setImportStats({
                        total: result.stats?.total || result.data?.total || validationResults.valid || 0,
                        imported: result.stats?.imported || result.data?.imported || validationResults.valid || 0,
                        failed: result.stats?.failed || result.data?.failed || validationResults.invalid || 0,
                        duplicates: result.stats?.duplicates || result.data?.duplicates || validationResults.duplicates || 0
                    });
                } catch {
                    setImportStats({
                        total: validationResults.valid || 0,
                        imported: validationResults.valid || 0,
                        failed: validationResults.invalid || 0,
                        duplicates: validationResults.duplicates || 0
                    });
                }
                setImportStatus('success');
                setUploadResults({ success: true });
                showToast('success', 'Import réussi', 'Les clients ont été importés avec succès');
            } else {
                throw new Error(`Erreur serveur: ${response.status}`);
            }
        } catch (error) {
            console.error('Erreur:', error);
            setImportStatus('error');
            setUploadResults({ success: false, message: error.message });
            showToast('error', 'Erreur', error.message || "Erreur lors de l'import");
        } finally {
            setImporting(false);
            setImportStep(3);
        }
    };

    const resetImport = () => {
        setImportStep(1);
        setSelectedFile(null);
        setFilePreview([]);
        setValidationResults({});
        setImportProgress(0);
        setImportStatus('idle');
        setImportStats({});
        setUploadResults(null);
        setImporting(false);
    };

    /* ============================================================
       RENDU
       ============================================================ */
    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-upload-cloud-2-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Importer des clients</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-file-line"></i>
                                        CSV / Excel
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-information-line"></i>
                                        Import par lot
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button as={Link} to="/ventes/clients/liste" className="btn-modern-outline">
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                            <Button onClick={downloadTemplateCSV} className="btn-modern-outline">
                                <i className="ri-file-text-line"></i> Modèle CSV
                            </Button>
                            <Button onClick={downloadTemplateExcel} className="btn-modern-success">
                                <i className="ri-file-excel-line"></i> Modèle Excel
                            </Button>
                        </div>
                    </div>

                    {/* ===== STEPPER ===== */}
                    <Stepper
                        step={importStep}
                        steps={['Import fichier', 'Validation', 'Résultats']}
                    />

                    {/* ===== ÉTAPE 1 : UPLOAD ===== */}
                    {importStep === 1 && (
                        <Row className="justify-content-center">
                            <Col lg={8}>
                                <Card className="border-0 shadow-sm rounded-3">
                                    <Card.Header className="card-header-gradient">
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-upload-cloud-2-line me-2"></i>
                                            Sélectionner le fichier
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div
                                            className={`upload-area-charte ${dragOver ? 'drag-over' : ''}`}
                                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                            onDragLeave={() => setDragOver(false)}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                setDragOver(false);
                                                const file = e.dataTransfer.files[0];
                                                if (file) handleFileSelect({ target: { files: [file] } });
                                            }}
                                            onClick={() => document.getElementById('fileInput').click()}
                                        >
                                            <div className="upload-icon-charte">
                                                <i className="ri-file-upload-line"></i>
                                            </div>
                                            <h5 className="upload-title-charte">Glissez-déposez votre fichier ici</h5>
                                            <p className="upload-subtitle-charte">
                                                ou cliquez pour parcourir vos fichiers
                                            </p>
                                            <div className="upload-formats-charte">
                                                <span className="format-tag-charte">.CSV</span>
                                                <span className="format-tag-charte">.XLSX</span>
                                                <span className="format-tag-charte">.XLS</span>
                                            </div>
                                            <Form.Control
                                                type="file"
                                                accept=".csv,.xlsx,.xls"
                                                onChange={handleFileSelect}
                                                className="d-none"
                                                id="fileInput"
                                            />
                                        </div>

                                        <div className="mt-4">
                                            <div className="section-title-charte">
                                                <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                    <i className="ri-file-list-line"></i>
                                                </span>
                                                <h6>Format attendu</h6>
                                            </div>
                                            <div className="table-responsive">
                                                <Table className="align-middle mb-0 table-charte">
                                                    <thead>
                                                        <tr>
                                                            <th>Colonne</th>
                                                            <th className="text-center">Obligatoire</th>
                                                            <th>Exemple</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {expectedColumns.map(column => (
                                                            <tr key={column.key}>
                                                                <td className="fw-semibold">{column.label}</td>
                                                                <td className="text-center">
                                                                    {column.required ? (
                                                                        <span className="badge-required-charte">
                                                                            <i className="ri-checkbox-circle-line"></i>Obligatoire
                                                                        </span>
                                                                    ) : (
                                                                        <span className="badge-optional-charte">
                                                                            <i className="ri-checkbox-blank-circle-line"></i>Optionnel
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <span className="reference-badge-charte">
                                                                        <i className="ri-code-line"></i>{column.example}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* ===== ÉTAPE 2 : VALIDATION ===== */}
                    {importStep === 2 && (
                        <Row className="justify-content-center">
                            <Col lg={10}>
                                <Card className="border-0 shadow-sm rounded-3">
                                    <Card.Header className="card-header-gradient">
                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                            <h5 className="mb-0 text-white">
                                                <i className="ri-checkbox-circle-line me-2"></i>
                                                Validation des données
                                            </h5>
                                            <span className="badge-header-light">
                                                <i className="ri-file-line"></i> {selectedFile?.name}
                                            </span>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        {importStatus === 'importing' && (
                                            <div className="text-center py-4">
                                                <div className="loading-spinner-charte mb-3">
                                                    <div className="loading-ring-charte"></div>
                                                    <i className="ri-cloud-line loading-icon-charte"></i>
                                                </div>
                                                <h5 className="fw-bold mb-2" style={{ color: COLORS.primary }}>
                                                    Import en cours...
                                                </h5>
                                                <p className="text-muted mb-4">
                                                    Veuillez patienter pendant l'importation des clients.
                                                </p>
                                                <div className="progress-charte-large">
                                                    <div className="progress-bar-charte-large" style={{ width: `${importProgress}%` }}>
                                                        <span>{importProgress}%</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {importStatus !== 'importing' && (
                                            <>
                                                {!validationResults.columnMatch && (
                                                    <Alert className="alert-warning-charte mb-4">
                                                        <div className="d-flex align-items-start gap-3">
                                                            <i className="ri-alert-line fs-4"></i>
                                                            <div>
                                                                <strong>Format de fichier incorrect</strong>
                                                                <div className="mt-1 small">
                                                                    Vérifiez que votre fichier contient les colonnes obligatoires :
                                                                    <strong className="ms-1">raison_sociale, telephone</strong>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Alert>
                                                )}

                                                <Row className="g-3 mb-4">
                                                    <Col sm="6" xl="3">
                                                        <div className="kpi-charte kpi-success">
                                                            <div className="kpi-icon-charte"><i className="ri-checkbox-circle-line"></i></div>
                                                            <div className="kpi-content-charte">
                                                                <span className="kpi-label-charte">Clients valides</span>
                                                                <h3 className="kpi-value-charte">{validationResults.valid || 0}</h3>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col sm="6" xl="3">
                                                        <div className="kpi-charte kpi-danger">
                                                            <div className="kpi-icon-charte"><i className="ri-error-warning-line"></i></div>
                                                            <div className="kpi-content-charte">
                                                                <span className="kpi-label-charte">Erreurs</span>
                                                                <h3 className="kpi-value-charte">{validationResults.invalid || 0}</h3>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col sm="6" xl="3">
                                                        <div className="kpi-charte kpi-warning">
                                                            <div className="kpi-icon-charte"><i className="ri-alert-line"></i></div>
                                                            <div className="kpi-content-charte">
                                                                <span className="kpi-label-charte">Doublons</span>
                                                                <h3 className="kpi-value-charte">{validationResults.duplicates || 0}</h3>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col sm="6" xl="3">
                                                        <div className="kpi-charte kpi-info">
                                                            <div className="kpi-icon-charte"><i className="ri-stack-line"></i></div>
                                                            <div className="kpi-content-charte">
                                                                <span className="kpi-label-charte">Total lignes</span>
                                                                <h3 className="kpi-value-charte">{filePreview.length - 1 || 0}</h3>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                {/* Config import */}
                                                <div className="config-box-charte mb-4">
                                                    <div className="section-title-charte">
                                                        <span className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                                            <i className="ri-settings-line"></i>
                                                        </span>
                                                        <h6>Configuration de l'import</h6>
                                                    </div>
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">Traitement par défaut</Form.Label>
                                                        <div className="d-flex gap-3 flex-wrap">
                                                            <div className={`type-choice-charte ${defaultType === 'entreprise' ? 'active' : ''}`}>
                                                                <Form.Check
                                                                    type="radio"
                                                                    id="type-entreprise"
                                                                    name="defaultType"
                                                                    value="entreprise"
                                                                    checked={defaultType === 'entreprise'}
                                                                    onChange={(e) => setDefaultType(e.target.value)}
                                                                    label={
                                                                        <span className="d-flex align-items-center gap-2">
                                                                            <i className="ri-building-line"></i> Entreprise
                                                                        </span>
                                                                    }
                                                                />
                                                            </div>
                                                            <div className={`type-choice-charte ${defaultType === 'particulier' ? 'active' : ''}`}>
                                                                <Form.Check
                                                                    type="radio"
                                                                    id="type-particulier"
                                                                    name="defaultType"
                                                                    value="particulier"
                                                                    checked={defaultType === 'particulier'}
                                                                    onChange={(e) => setDefaultType(e.target.value)}
                                                                    label={
                                                                        <span className="d-flex align-items-center gap-2">
                                                                            <i className="ri-user-line"></i> Particulier
                                                                        </span>
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                        <Form.Text className="text-muted small">
                                                            Les lignes sans code client seront traitées comme des {defaultType === 'particulier' ? 'particuliers' : 'entreprises'}
                                                        </Form.Text>
                                                    </Form.Group>
                                                </div>

                                                {/* Aperçu */}
                                                <div className="mb-4">
                                                    <div className="section-title-charte">
                                                        <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                            <i className="ri-eye-line"></i>
                                                        </span>
                                                        <h6>Aperçu des 10 premières lignes</h6>
                                                    </div>
                                                    <div className="table-responsive">
                                                        <Table className="align-middle mb-0 table-charte">
                                                            <thead>
                                                                <tr>
                                                                    {filePreview[0]?.map((header, index) => (
                                                                        <th key={index}>{header || `Colonne ${index + 1}`}</th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {filePreview.slice(1, 11).map((row, rowIndex) => (
                                                                    <tr key={rowIndex}>
                                                                        {row.map((cell, cellIndex) => {
                                                                            const isRequired = cellIndex === 1 || cellIndex === 2;
                                                                            const isValid = isRequired ? cell?.toString().trim() : true;
                                                                            return (
                                                                                <td key={cellIndex} className={!isValid ? 'cell-error-charte' : ''}>
                                                                                    {cell || <span className="text-muted fst-italic">vide</span>}
                                                                                </td>
                                                                            );
                                                                        })}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                </div>

                                                {/* Erreurs */}
                                                {validationResults.errors?.length > 0 && (
                                                    <div className="mb-4">
                                                        <div className="section-title-charte">
                                                            <span className="section-icon-charte" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                                                <i className="ri-error-warning-line"></i>
                                                            </span>
                                                            <h6>Erreurs de validation ({validationResults.errors.length})</h6>
                                                        </div>
                                                        <div className="errors-box-charte errors-danger-charte">
                                                            {validationResults.errors.slice(0, 10).map((error, index) => (
                                                                <div key={index} className="error-item-charte">
                                                                    <i className="ri-close-circle-line"></i>
                                                                    <span>{error}</span>
                                                                </div>
                                                            ))}
                                                            {validationResults.errors.length > 10 && (
                                                                <div className="error-more-charte">
                                                                    ... et {validationResults.errors.length - 10} autres erreurs
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Avertissements */}
                                                {validationResults.warnings?.length > 0 && (
                                                    <div className="mb-4">
                                                        <div className="section-title-charte">
                                                            <span className="section-icon-charte" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                                                <i className="ri-alert-line"></i>
                                                            </span>
                                                            <h6>Avertissements ({validationResults.warnings.length})</h6>
                                                        </div>
                                                        <div className="errors-box-charte errors-warning-charte">
                                                            {validationResults.warnings.slice(0, 5).map((warning, index) => (
                                                                <div key={index} className="error-item-charte">
                                                                    <i className="ri-information-line"></i>
                                                                    <span>{warning}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Actions */}
                                                <div className="d-flex justify-content-between flex-wrap gap-2">
                                                    <Button
                                                        onClick={() => setImportStep(1)}
                                                        disabled={importing}
                                                        className="btn-modern-outline"
                                                    >
                                                        <i className="ri-arrow-left-line me-2"></i>Retour
                                                    </Button>
                                                    <Button
                                                        onClick={importClients}
                                                        disabled={!validationResults.columnMatch || validationResults.valid === 0 || importing}
                                                        className="btn-modern-primary"
                                                    >
                                                        {importing ? (
                                                            <><Spinner size="sm" className="me-2" />Import en cours...</>
                                                        ) : (
                                                            <>Démarrer l'import<i className="ri-arrow-right-line ms-2"></i></>
                                                        )}
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* ===== ÉTAPE 3 : RÉSULTATS ===== */}
                    {importStep === 3 && (
                        <Row className="justify-content-center">
                            <Col lg={8}>
                                <Card className="border-0 shadow-sm rounded-3">
                                    <Card.Header
                                        className="card-header-gradient"
                                        style={{
                                            background: importStatus === 'success'
                                                ? `linear-gradient(135deg, ${COLORS.success}, #059669)`
                                                : `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`
                                        }}
                                    >
                                        <h5 className="mb-0 text-white">
                                            <i className={`ri-${importStatus === 'success' ? 'checkbox-circle' : 'error-warning'}-line me-2`}></i>
                                            {importStatus === 'success' ? 'Import terminé avec succès' : "Erreur lors de l'import"}
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4 text-center">
                                        {importStatus === 'success' ? (
                                            <>
                                                <div className="success-icon-charte mb-4">
                                                    <i className="ri-checkbox-circle-line"></i>
                                                </div>
                                                <h4 className="fw-bold mb-2" style={{ color: COLORS.primaryDark }}>Import réussi !</h4>
                                                <p className="text-muted mb-4">
                                                    {importStats.imported || 0} clients importés avec succès
                                                </p>

                                                <Row className="g-3 mb-4">
                                                    <Col sm="4">
                                                        <div className="kpi-charte kpi-success">
                                                            <div className="kpi-icon-charte"><i className="ri-download-line"></i></div>
                                                            <div className="kpi-content-charte">
                                                                <span className="kpi-label-charte">Importés</span>
                                                                <h3 className="kpi-value-charte">{importStats.imported || 0}</h3>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col sm="4">
                                                        <div className="kpi-charte kpi-warning">
                                                            <div className="kpi-icon-charte"><i className="ri-alert-line"></i></div>
                                                            <div className="kpi-content-charte">
                                                                <span className="kpi-label-charte">Doublons</span>
                                                                <h3 className="kpi-value-charte">{importStats.duplicates || 0}</h3>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col sm="4">
                                                        <div className="kpi-charte kpi-info">
                                                            <div className="kpi-icon-charte"><i className="ri-stack-line"></i></div>
                                                            <div className="kpi-content-charte">
                                                                <span className="kpi-label-charte">Total lignes</span>
                                                                <h3 className="kpi-value-charte">{importStats.total || 0}</h3>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                <div className="d-flex justify-content-center gap-3 flex-wrap">
                                                    <Button onClick={resetImport} className="btn-modern-outline">
                                                        <i className="ri-add-line me-2"></i>Nouvel import
                                                    </Button>
                                                    <Button as={Link} to="/ventes/clients/liste" className="btn-modern-primary">
                                                        <i className="ri-group-line me-2"></i>Voir les clients
                                                    </Button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="error-icon-charte mb-4">
                                                    <i className="ri-error-warning-line"></i>
                                                </div>
                                                <h4 className="fw-bold mb-2" style={{ color: COLORS.danger }}>Une erreur est survenue</h4>
                                                <p className="text-muted mb-4">
                                                    {uploadResults?.message || "L'import n'a pas pu être effectué"}
                                                </p>
                                                <div className="d-flex justify-content-center gap-3 flex-wrap">
                                                    <Button onClick={() => setImportStep(2)} className="btn-modern-outline">
                                                        <i className="ri-arrow-left-line me-2"></i>Retour
                                                    </Button>
                                                    <Button onClick={resetImport} className="btn-modern-primary">
                                                        <i className="ri-refresh-line me-2"></i>Réessayer
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }

                /* ===== STEPPER ===== */
                .stepper-modern {
                    background: white; padding: 1.5rem 2rem;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                }
                .step-item-charte {
                    display: flex; flex-direction: column; align-items: center;
                    position: relative; z-index: 2; min-width: 100px;
                }
                .step-number-charte {
                    width: 48px; height: 48px;
                    background: #e2e8f0;
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-weight: 700; color: #64748b; font-size: 1.1rem;
                    transition: all 0.3s ease;
                }
                .step-item-charte.active .step-number-charte {
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    color: white;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.3);
                    transform: scale(1.05);
                }
                .step-item-charte.completed .step-number-charte {
                    background: linear-gradient(135deg, var(--success), #059669);
                    color: white;
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
                }
                .step-label-charte {
                    font-size: 0.75rem; margin-top: 0.6rem;
                    color: #64748b; font-weight: 500; text-align: center;
                    transition: color 0.3s;
                }
                .step-item-charte.active .step-label-charte { color: var(--primary); font-weight: 600; }
                .step-item-charte.completed .step-label-charte { color: var(--success); }
                .step-line-charte {
                    flex: 1; height: 3px; background: #e2e8f0;
                    margin: 0 0.5rem; border-radius: 2px;
                    transition: background 0.3s;
                    align-self: flex-start; margin-top: 22px;
                }
                .step-line-charte.completed {
                    background: linear-gradient(90deg, var(--success), #059669);
                }

                /* ===== UPLOAD AREA ===== */
                .upload-area-charte {
                    border: 2px dashed #cbd5e1;
                    border-radius: 16px;
                    padding: 3rem 2rem;
                    text-align: center;
                    background: #f8fafc;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }
                .upload-area-charte:hover,
                .upload-area-charte.drag-over {
                    border-color: var(--secondary);
                    background: linear-gradient(135deg, rgba(41,90,167,0.03), rgba(33,172,205,0.05));
                    transform: scale(1.005);
                }
                .upload-icon-charte {
                    width: 90px; height: 90px;
                    background: linear-gradient(135deg, ${COLORS.primary}15, ${COLORS.secondary}20);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1.5rem;
                    font-size: 2.5rem; color: var(--secondary);
                }
                .upload-title-charte {
                    font-size: 1.15rem; font-weight: 700; color: #1e293b;
                    margin-bottom: 0.5rem;
                }
                .upload-subtitle-charte {
                    font-size: 0.9rem; color: #64748b;
                    margin-bottom: 1.5rem;
                }
                .upload-formats-charte {
                    display: inline-flex; gap: 0.5rem; flex-wrap: wrap;
                    justify-content: center;
                }
                .format-tag-charte {
                    background: white; color: var(--primary);
                    border: 1.5px solid ${COLORS.primary}20;
                    padding: 0.3rem 0.9rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    font-family: monospace;
                }

                /* ===== SECTION ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.85rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .cell-error-charte {
                    background: #fef2f2 !important;
                    color: #991b1b;
                }
                .badge-required-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: ${COLORS.danger}15; color: var(--danger);
                    padding: 0.25rem 0.7rem; border-radius: 50px;
                    font-size: 0.7rem; font-weight: 600;
                }
                .badge-optional-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; color: #64748b;
                    padding: 0.25rem 0.7rem; border-radius: 50px;
                    font-size: 0.7rem; font-weight: 600;
                }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }

                /* ===== CONFIG BOX ===== */
                .config-box-charte {
                    background: #f8fafc;
                    padding: 1.25rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .type-choice-charte {
                    padding: 0.75rem 1.25rem;
                    background: white;
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    transition: all 0.2s ease;
                    min-width: 160px;
                }
                .type-choice-charte:hover {
                    border-color: var(--secondary);
                    background: #f0fbff;
                }
                .type-choice-charte.active {
                    border-color: var(--secondary);
                    background: linear-gradient(135deg, rgba(41,90,167,0.05), rgba(33,172,205,0.1));
                    box-shadow: 0 4px 12px rgba(33,172,205,0.15);
                }
                .type-choice-charte .form-check {
                    margin: 0;
                }
                .type-choice-charte .form-check-label {
                    font-weight: 600;
                    color: #334155;
                    cursor: pointer;
                }
                .type-choice-charte .form-check-input:checked {
                    background-color: var(--secondary);
                    border-color: var(--secondary);
                }

                /* ===== ERRORS BOX ===== */
                .errors-box-charte {
                    border-radius: 12px;
                    padding: 0.75rem 1rem;
                    max-height: 200px;
                    overflow-y: auto;
                }
                .errors-danger-charte {
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                }
                .errors-warning-charte {
                    background: #fffbeb;
                    border: 1px solid #fed7aa;
                }
                .error-item-charte {
                    display: flex; align-items: flex-start; gap: 0.5rem;
                    padding: 0.5rem 0;
                    border-bottom: 1px dashed rgba(0,0,0,0.05);
                    font-size: 0.8rem;
                }
                .error-item-charte:last-child { border-bottom: none; }
                .errors-danger-charte .error-item-charte { color: #991b1b; }
                .errors-warning-charte .error-item-charte { color: #92400e; }
                .error-more-charte {
                    padding-top: 0.5rem; text-align: center;
                    font-size: 0.75rem; color: #94a3b8; font-style: italic;
                }

                /* ===== PROGRESS ===== */
                .progress-charte-large {
                    height: 32px;
                    background: #e2e8f0;
                    border-radius: 50px;
                    overflow: hidden;
                    box-shadow: inset 0 2px 4px rgba(0,0,0,0.05);
                }
                .progress-bar-charte-large {
                    height: 100%;
                    background: linear-gradient(90deg, var(--primary), var(--secondary));
                    border-radius: 50px;
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-weight: 700; font-size: 0.85rem;
                    transition: width 0.4s ease;
                    box-shadow: 0 2px 8px rgba(41,90,167,0.3);
                }

                /* ===== SUCCESS / ERROR ICON ===== */
                .success-icon-charte {
                    width: 110px; height: 110px;
                    background: linear-gradient(135deg, ${COLORS.success}15, ${COLORS.success}05);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto;
                    animation: scaleIn 0.5s ease-out;
                }
                .success-icon-charte i {
                    font-size: 4rem; color: var(--success);
                }
                .error-icon-charte {
                    width: 110px; height: 110px;
                    background: linear-gradient(135deg, ${COLORS.danger}15, ${COLORS.danger}05);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto;
                    animation: scaleIn 0.5s ease-out;
                }
                .error-icon-charte i {
                    font-size: 4rem; color: var(--danger);
                }
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.15); }
                    100% { transform: scale(1); opacity: 1; }
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== ALERTES ===== */
                .alert-warning-charte {
                    border: none; background: #fffbeb;
                    border-left: 4px solid var(--warning);
                    border-radius: 12px; padding: 1rem 1.25rem;
                    color: #92400e;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary, .btn-modern-success {
                    border: none; color: white; padding: 0.55rem 1.8rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-primary:disabled,
                .btn-modern-outline:disabled,
                .btn-modern-success:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .step-label-charte { font-size: 0.65rem; }
                    .step-number-charte { width: 40px; height: 40px; font-size: 0.95rem; }
                    .step-item-charte { min-width: 70px; }
                    .stepper-modern { padding: 1rem; }
                    .upload-area-charte { padding: 2rem 1rem; }
                    .upload-icon-charte { width: 70px; height: 70px; font-size: 2rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </React.Fragment>
    );
}